import { faCheckCircle, faLink } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import queryString, { type StringifiableRecord } from 'query-string';
import { type KeyboardEvent, useCallback, useEffect, useId, useRef, useState } from 'react';

import { Button, type ButtonProps } from '../Button/Button';
import * as style from './CopyField.css';

type CopyFieldProps = {
  buttonText?: {
    copy: string;
    copied: string;
  };
  value: string;
  noButtonLabel?: boolean;
  /**
   * Renders just the copy button, without the read-only URL field. For compact layouts
   * where the link itself isn't shown (e.g. a template picker paired with "Copy link").
   */
  buttonOnly?: boolean;
  /** Disables the copy button. Only honoured by `buttonOnly`. */
  disabled?: boolean;
  /** Size of the copy button. Only honoured by `buttonOnly`; defaults to `regular`. */
  buttonSize?: ButtonProps['size'];
  afterCopy?: (value: string) => void;
  extraParams?: StringifiableRecord;
  onCopyFailure?: (text: string) => void;
  className?: string;
};

export const CopyField = ({
  value,
  buttonText = { copy: 'Copy', copied: 'Copied' },
  afterCopy,
  noButtonLabel,
  buttonOnly,
  disabled,
  buttonSize,
  extraParams,
  onCopyFailure,
  className,
}: CopyFieldProps) => {
  const copyStatusId = useId();
  const ref = useRef<HTMLInputElement>(null);
  const { copied, markAsCopied } = useCopiedState(afterCopy);

  /** Builds the actual value written to the clipboard */
  const buildCopyValue = useCallback(() => {
    const { url: baseUrl, query } = queryString.parseUrl(value);

    const newQuery = {
      ...query,
      ...extraParams,
    };

    return queryString.stringifyUrl({
      url: baseUrl,
      query: newQuery,
    });
  }, [value, extraParams]);

  const selectAll = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    requestAnimationFrame(() => {
      el.select();
      try {
        el.setSelectionRange(0, value.length);
      } catch {
        // Do nothing
      }
      el.scrollLeft = el.scrollWidth;
    });
  }, [value]);

  const doCopy = useCallback(async () => {
    const finalValue = buildCopyValue();
    selectAll();

    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(finalValue);
        markAsCopied(finalValue);
        return;
      }
    } catch {
      onCopyFailure?.(finalValue);
    }
  }, [buildCopyValue, selectAll, markAsCopied, onCopyFailure]);

  /** Keyboard copies — now also apply extraParams */
  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    // Cmd/Ctrl + C
    if (((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'c') || e.key === 'Enter') {
      e.preventDefault();
      void doCopy();
      return;
    }
  };

  if (buttonOnly) {
    return (
      <div className={className}>
        <Button
          variant="brand"
          type="button"
          size={buttonSize}
          iconLeading={<FontAwesomeIcon icon={copied ? faCheckCircle : faLink} />}
          onClick={doCopy}
          disabled={disabled}
        >
          {copied ? buttonText.copied : buttonText.copy}
        </Button>
        <span id={copyStatusId} aria-live="polite" aria-atomic="true" className={style.srOnly}>
          {copied ? 'Copied to clipboard' : ''}
        </span>
      </div>
    );
  }

  return (
    <div className={[style.wrapperStyle, className].filter(Boolean).join(' ')}>
      <input
        ref={ref}
        value={value} // unchanged UI
        readOnly
        title={value}
        dir="ltr"
        spellCheck={false}
        autoCapitalize="off"
        autoCorrect="off"
        className={style.inputStyle}
        onFocus={selectAll}
        onMouseUp={(e) => e.preventDefault()}
        onKeyDown={onKeyDown}
        aria-describedby={copyStatusId}
      />
      {noButtonLabel ? (
        <Button
          variant="brand"
          type="button"
          iconLeading={<FontAwesomeIcon icon={copied ? faCheckCircle : faLink} />}
          onClick={doCopy}
          size="iconOnly"
        />
      ) : (
        <Button
          variant="brand"
          type="button"
          iconLeading={<FontAwesomeIcon icon={faLink} />}
          onClick={doCopy}
        >
          {copied ? buttonText.copied : buttonText.copy}
        </Button>
      )}
      <span id={copyStatusId} aria-live="polite" aria-atomic="true" className={style.srOnly}>
        {copied ? 'Copied to clipboard' : ''}
      </span>
    </div>
  );
};

function useCopiedState(afterCopy?: (value: string) => void) {
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  const markAsCopied = useCallback(
    (value: string) => {
      setCopied(true);
      afterCopy?.(value);

      if (timeoutRef.current) clearTimeout(timeoutRef.current);

      timeoutRef.current = setTimeout(() => {
        setCopied(false);
        timeoutRef.current = null;
      }, 1800);
    },
    [afterCopy],
  );

  return { copied, markAsCopied };
}
